/**
 * Standard Javascript error object that should be used for
 * propagating, operating and logging of errors in marketplace-frontend
 **/
import nanoid from 'nanoid';

/**
 * Wraps a passed function after dynamically
 * setting the function name. The function name
 * is minification-proof.
 */
// eslint-disable-next-line flowtype/no-weak-types
function withName(name, fn) {
  // eslint-disable-next-line flowtype/no-weak-types
  const wrappedFunction = new Function("return function (call) { \n         return function ".concat(name, "() { \n           return call(this, arguments) \n         }; \n      };")); // $FlowFixMe: Unable to find a good way to type the bind here

  return wrappedFunction()(Function.apply.bind(fn));
} // If a transform function is specified, the fields passed
// to the error constructor should match fields passed to the
// transform function. Else they should match standard `ErrorFields`


/**
 * Factory function for creating new error types.
 * Takes an optional transform function that is expected
 * to return standard ErrorFields.
 *
 * @example
 * const SimpleError = createErrorType('SimpleError')
 * throw new SimpleError({ code: 'code', message: 'something went wrong'})
 * ----
 * type BuiltErrorFields = { foo: string }
 * const BuiltError =  createErrorType<BuiltErrorFields>('BuiltError', ({ foo }) => ({ code: foo, message: 'bar' }))
 * throw new BuiltError({ foo: 'baz'})
 */
const createErrorType = (type, transform) => {
  const CustomError = withName(type, function (params) {
    const finalParams = transform ? transform(params) : params;
    const {
      code,
      message,
      meta,
      source,
      ...other
    } = finalParams;
    this.code = code;
    this.message = message;
    this.id = nanoid(9);
    this.type = type;
    this.source = source;
    this.meta = { ...meta,
      ...other
    };

    this.toJSON = () => ({
      id: this.id,
      code: this.code,
      type: this.type,
      message: this.message,
      meta: this.meta,
      ...(this.source && {
        source: this.source.toJSON ? this.source.toJSON() : this.source.message ? this.source.message : this.source
      })
    });

    this.toString = () => JSON.stringify(this.toJSON());

    if (Object.prototype.hasOwnProperty.call(Error, 'captureStackTrace')) {
      Error.captureStackTrace(this, CustomError);
    } else {
      this.stack = new Error(message).stack;
    }
  });

  if (typeof Object.setPrototypeOf === 'function') {
    Object.setPrototypeOf(CustomError.prototype, Error.prototype);
  } else {
    CustomError.prototype = Object.create(Error.prototype, {
      constructor: {
        value: CustomError
      }
    });
  }

  return CustomError;
};

export default createErrorType;